import { queryOptions, useQuery } from '@tanstack/vue-query';
import { computed } from 'vue';

import { getUserMe, getUserScopeMe, getUserScopeProducts } from './services';

export type SocialProvider = {
  socialId: number;
  provider: 'GOOGLE' | 'CLEVER';
  email: string;
  username: string;
};

export type DistributorCompany = {
  options?: unknown;
  active?: unknown;
  distributorCompanyId?: unknown;
  name?: string;
  imageUrl?: string;
  code?: string;
};

export type StudentDetails = {
  schoolImageURL: string;
  schoolName: string;
  edLevel: 'PRIMARY_SCHOOL' | 'MIDDLE_SCHOOL' | 'HIGH_SCHOOL';
};

export type AddressInformation = {
  addressId: number;
  countryId: number;
  country: string;
  cityId: number;
  city: string;
};

export type Membership = {
  orderType: 'NO_ORDER' | 'INDIVIDUAL' | 'SCHOOL';
  subscription: boolean;
  repetitionCount: number;
};

export type TeacherDetails = {
  teacherId: number;
  parent: boolean;
  paidLicenceLimit: number;
  freeLicenceLimit: number;
  paidStudentsCount: number;
  usedPaidLicence: number;
  freeStudentsCount: number;
  userUuid: string;
  firstName: string;
  lastName: string;
  email: string;
  maxClassStudentLimit: number;
  headTeacher: boolean;
  schoolId?: number;
  schoolImageURL?: string | null;
  schoolName?: string | null;
};

export type Account = {
  uuid?: string;
  settings: unknown;
  type: string;
  avatar?: string;
  username?: string;
  lastName?: string | null;
  firstName?: string | null;
  paymentType: 'FREE' | 'PAID' | 'TRIAL' | 'STAFF';
  ipApiCountryCode?: string;
  ipApiCountryName?: string;
  ipApiRegion?: string;
};

export type ScopeTutorial = {
  tutorialId: number;
  pages: number[];
};

export type ScopeProduct = {
  productType: string;
  tutorials: ScopeTutorial[];
};
export interface UserMeApiData {
  account: Account;
  distributorCompany?: DistributorCompany;
  studentDetails?: StudentDetails;
  teacherDetails?: TeacherDetails;
  address?: AddressInformation;
  membership?: Membership;
  socialAccounts?: SocialProvider[];
  instructorDetails?: Record<string, unknown>;
  managerDetails?: Record<string, unknown>;
}

interface UserMeEnvelope {
  success: boolean;
  data: UserMeApiData;
}

export function useAuthUserOptions() {
  return queryOptions({
    queryKey: ['user'],
    queryFn: () => getUserMe() as unknown as Promise<UserMeEnvelope>,
    select: ({ data }: UserMeEnvelope) => {
      try {
        const settingsRaw = data.account.settings as unknown as string;
        data.account.settings = JSON.parse(settingsRaw);
      } catch {
        data.account.settings = { language: 'en' };
      }

      try {
        if (data.distributorCompany) {
          const options = data.distributorCompany.options as unknown as string;
          data.distributorCompany.options = JSON.parse(options);
        }
      } catch {
        /* ignore */
      }

      return data;
    }
  });
}

export function useAuthUser() {
  return useQuery<UserMeEnvelope, Error, UserMeApiData>(useAuthUserOptions());
}

export function useAccount() {
  const { data } = useAuthUser();

  return computed(() => {
    const account = data.value?.account;
    const student = data.value?.studentDetails;
    const teacher = data.value?.teacherDetails;

    const firstName = account?.firstName || '';
    const lastName = account?.lastName || '';
    let fullName = `${firstName} ${lastName}`.trim();
    if (!fullName) fullName = account?.username ?? '';

    const schoolImageURL =
      student?.schoolImageURL ?? teacher?.schoolImageURL ?? null;
    const schoolName = student?.schoolName ?? teacher?.schoolName ?? null;

    let initials = '';
    if (firstName) initials += firstName[0];
    if (lastName) initials += lastName[0];
    initials = initials.toUpperCase();

    return {
      ...account,
      fullName,
      initials,
      schoolImageURL,
      schoolName,
      isAuthenticated: !!account?.type,
      isFromTR: account?.ipApiCountryName === 'Turkey',
      isStudent: account?.type === 'STUDENT',
      isParent: account?.type === 'PARENT',
      isTeacher: account?.type === 'TEACHER',
      isDeveloper: account?.type === 'DEVELOPER',
      isAdmin: account?.type === 'ADMIN',
      isManager: account?.type === 'MANAGER',
      isDistributor: account?.type === 'DISTRIBUTOR',
      isPaid: account?.paymentType === 'PAID',
      isTrial: account?.paymentType === 'TRIAL',
      isFree: account?.paymentType === 'FREE',
      isStaff: account?.paymentType === 'STAFF'
    };
  });
}

export function useDistributorCompany() {
  const { data } = useAuthUser();

  return computed(() => {
    const distributorCompany = data.value?.distributorCompany;
    if (!distributorCompany) return null;

    const opts = distributorCompany.options as { css?: unknown };
    return {
      ...distributorCompany,
      hasDistributor: !!(
        distributorCompany?.active && distributorCompany?.distributorCompanyId
      ),
      distributorName: distributorCompany?.name,
      distributorImage: distributorCompany?.imageUrl,
      distributorOptions: distributorCompany?.options,
      distributorOptionsCSS: opts?.css,
      isPrivateDistributor: [
        'CODINGOLYMPIADORG',
        'CODEMINATOR',
        'GLOW'
      ].includes(distributorCompany?.code ?? ''),
      distributorCompanyCode: distributorCompany?.code
    };
  });
}

export function useTeacherDetails() {
  const { data } = useAuthUser();

  return computed(() => {
    const teacherDetails = data.value?.teacherDetails;
    if (!teacherDetails) return null;

    const socialAccounts = data.value?.socialAccounts;
    const isClever = Array.isArray(socialAccounts)
      ? Boolean(socialAccounts.find((item) => item.provider === 'CLEVER'))
      : false;

    return { ...teacherDetails, isClever };
  });
}

export function useUserScopeProducts() {
  const { data } = useAuthUser();

  const userUuid = computed(() => data.value?.account?.uuid);

  return useQuery({
    queryKey: ['user', 'scope', 'products', userUuid],
    queryFn: () =>
      getUserScopeProducts(userUuid.value!).then(
        (res: any) => (res.data?.products ?? res.data ?? []) as ScopeProduct[]
      ),
    enabled: computed(() => !!userUuid.value),
    staleTime: 5 * 60 * 1000
  });
}

export function userScopeMeOptions() {
  return queryOptions({
    queryKey: ['user', 'scope', 'me'],
    queryFn: () => getUserScopeMe(),
    staleTime: 10 * 60 * 1000
  });
}
